<template>
	<div>
		<b-breadcrumb>
			<b-breadcrumb-item to="/">{{$title}}</b-breadcrumb-item>
			<b-breadcrumb-item active>Commands</b-breadcrumb-item>
		</b-breadcrumb>
		<div class="text-center" v-if="loadingCommands>0">
			<b-icon icon="circle-fill" animation="throb" font-scale="4"></b-icon>
		</div>
		<b-alert show v-else-if="error" variant="danger">{{error&&(error.error||error.toString())}}</b-alert>
		<template v-else>
			<b-list-group>
				<b-list-group-item v-for="(command, idx) in commands" :key="idx" class="d-flex align-items-center command-item">
					<div class="flex-fill" style="min-width:0;overflow:hidden">
						<b-form-checkbox switch v-model="command.status" value="enabled" unchecked-value="disabled" @input="setCommandStatus(command)" :disabled="command.changingStatus || command.subscription_required">
							<code :style="{textDecoration:command.name_override&&command.name_override!=command.name&&'line-through'}">!{{command.name}}</code><code v-if="command.name_override&&command.name_override!=command.name">&nbsp;!{{command.name_override}}</code>
							<b-badge v-if="command.subscription_required" variant="warning" class="ml-1" style="cursor:pointer" @click.prevent="$root.$emit('show-upgrade-modal')">Upgrade</b-badge>
							<b-badge v-if="command.data && command.data.webhook && command.data.webhook.enabled && command.data.webhook.url" variant="info" class="ml-1"><b-icon icon="globe" class="mr-1"></b-icon>Webhook</b-badge>
							<small class="text-muted"> {{command.min_params == -1 ? (command.max_params == -1 ? 'Any' : ` at most ${command.max_params}`) : (command.max_params == -1 ? `at least ${command.min_params}` : (command.max_params == command.min_params ? `${command.min_params}` : `between ${command.min_params} and ${command.max_params}`))}} argument{{command.min_params > 1 || command.max_params > 1 ? 's' : ''}}</small>
						</b-form-checkbox>
						{{command.help_override||command.help}}
					</div>
					<b-button-group size="sm" class="flex-shrink-0 ml-2">
						<b-button @click="command.isCustom ? openUserCmdEditModal(command.name) : openCommandEditModal(command.name)" v-b-tooltip.hover.left title="Edit command"><b-icon icon="pencil-fill"></b-icon></b-button>
						<b-button v-if="command.isCustom" variant="danger" @click="openUserCmdDeleteModal(command.name)" v-b-tooltip.hover.right.v-danger title="Delete command"><b-icon icon="trash"></b-icon></b-button>
					</b-button-group>
				</b-list-group-item>
			</b-list-group>
		</template>
		<div class="text-center mt-3" v-if="!loadingCommands && !error">
			<b-button @click="openUserCmdEditModal()" variant="success" :disabled="user.id && !user.is_subscribed"><b-icon icon="plus"></b-icon> Add custom command</b-button>
			<small v-if="user.id && !user.is_subscribed" class="text-muted d-block mt-2">
				Custom commands are available for subscribed users. <b-badge variant="warning" style="cursor:pointer" @click="$root.$emit('show-upgrade-modal')">Upgrade</b-badge> to create your own commands, including webhook-based commands for <b-badge variant="warning" style="cursor:pointer" @click="$root.$emit('show-upgrade-modal')">Pro</b-badge> users.
			</small>
			<small v-else-if="user.id && !user.is_pro" class="text-muted d-block mt-2">
				<b-icon icon="globe" class="mr-1"></b-icon>Webhook-based commands are available on the Pro plan. <b-badge variant="warning" style="cursor:pointer" @click="$root.$emit('show-upgrade-modal')">Upgrade</b-badge>
			</small>
		</div>
		<b-modal ref="commandEditModal" :title="`Edit command`" :busy="userCmdLoading>0">
			<b-form @submit.stop.prevent="saveCommand(); return false" id="userCommandEditForm" v-if="curCmd">
				<b-form-group label="Name override">
					<b-form-input v-model="curCmd.name_override" maxlength="20"></b-form-input>
				</b-form-group>
				<b-form-group label="Help override">
					<b-form-input v-model="curCmd.help_override"></b-form-input>
				</b-form-group>
				<b-form-group label="Status">
					<b-form-radio-group
						v-model="curCmd.status"
						:options="statuses"
						required
					></b-form-radio-group>
				</b-form-group>
			</b-form>
			<b-alert variant="danger" :show="!!commandError">{{commandError}}</b-alert>
			<template #modal-footer="{ ok, cancel }">
				<b-spinner v-show="userCmdLoading>0" small label="Spinning"></b-spinner>
				<b-button size="sm" variant="success" type="submit" form="userCommandEditForm">
					Save
				</b-button>
				<b-button size="sm" @click="cancel()">
					Cancel
				</b-button>
			</template>
		</b-modal>
		<b-modal ref="userCmdEditModal" :title="`${userCmdName ? 'Edit' : 'Add'} custom command`" :busy="userCmdLoading>0" size="lg">
			<b-form @submit.stop.prevent="saveUserCmd(); return false" id="userCommandEditForm" v-if="curUserCmd">
				<b-form-group label="Name" description="The command string, without the exclamation mark">
					<b-form-input v-model="curUserCmd.name" required maxlength="20"></b-form-input>
				</b-form-group>
				<b-form-row>
					<b-col>
						<b-form-group label="Minimum arguments" description="use -1 for no minimum">
							<b-form-input v-model="curUserCmd.min_params" type="number" required min="-1"></b-form-input>
						</b-form-group>
					</b-col>
					<b-col>
						<b-form-group label="Maximum arguments" description="use -1 for no maximum">
							<b-form-input v-model="curUserCmd.max_params" type="number" required min="-1"></b-form-input>
						</b-form-group>
					</b-col>
				</b-form-row>
				<b-form-group label="Replies" description="Enter one reply per line. Replies are chosen randomly each time the command is invoked. Use $1, $2 for inserting arguments, and $* for all arguments. Special variables include $chatter, $channel, $service.">
					<b-form-textarea v-model="curUserCmd.data.replies"></b-form-textarea>
				</b-form-group>
				<b-form-group label="Help">
					<b-form-input v-model="curUserCmd.help"></b-form-input>
				</b-form-group>
				<b-form-group label="Status" v-if="user.is_subscribed">
					<b-form-radio-group
						v-model="curUserCmd.status"
						:options="statuses"
						required
					></b-form-radio-group>
				</b-form-group>

				<fieldset :disabled="!user.is_pro">
					<b-form-checkbox v-model="curUserCmd.data.webhook.enabled" class="mb-3">
						Enable webhook (replaces reply text with HTTP response)
						<b-badge v-if="!user.is_pro" variant="warning" class="ml-1" style="cursor:pointer" @click.prevent="$root.$emit('show-upgrade-modal')">PRO</b-badge>
					</b-form-checkbox>
				</fieldset>
				<fieldset :disabled="!user.is_pro || !curUserCmd.data.webhook.enabled">
					<b-form-group label="Method">
						<b-form-select v-model="curUserCmd.data.webhook.method" :options="httpMethods"></b-form-select>
					</b-form-group>
					<b-form-group label="URL" description="Supports $1, $2, $*, $chatter, $channel, $service">
						<b-form-input v-model="curUserCmd.data.webhook.url" placeholder="https://example.com/command"></b-form-input>
					</b-form-group>
					<b-form-group label="Content-Type">
						<b-form-select v-model="curUserCmd.data.webhook.content_type" :options="contentTypes"></b-form-select>
					</b-form-group>
					<b-form-group label="Request body" description="Supports $1, $2, $*, $chatter, $channel, $service. Leave empty for no body.">
						<b-form-textarea v-model="curUserCmd.data.webhook.body" rows="3"></b-form-textarea>
					</b-form-group>
					<b-form-group label="Custom headers">
						<div v-for="(header, idx) in curUserCmd.data.webhook.headers" :key="idx" class="d-flex mb-1" style="gap:0.5rem">
							<b-form-input v-model="header.name" placeholder="Header name" style="flex:1"></b-form-input>
							<b-form-input v-model="header.value" placeholder="Value (supports $chatter etc.)" style="flex:2"></b-form-input>
							<b-button size="sm" variant="danger" @click="removeWebhookHeader(idx)" :disabled="!user.is_pro || !curUserCmd.data.webhook.enabled"><b-icon icon="trash"></b-icon></b-button>
						</div>
						<b-button size="sm" variant="secondary" @click="addWebhookHeader()" :disabled="!user.is_pro || !curUserCmd.data.webhook.enabled"><b-icon icon="plus"></b-icon> Add header</b-button>
					</b-form-group>
					<b-form-group label="Signature secret" description="If set, adds an X-Signature: sha256=<hmac> header signed with this secret over the request body.">
						<b-form-input v-model="curUserCmd.data.webhook.secret" placeholder="Leave empty to disable signing"></b-form-input>
					</b-form-group>
					<small class="text-muted">Response is truncated to 50 characters. For <strong>text/plain</strong>, the response text is used as the reply. For <strong>application/json</strong>, the <code>response</code> string field is used. If no response is returned, falls back to the Replies field above.</small>
				</fieldset>
			</b-form>
			<b-alert variant="danger" :show="!!userCmdError">{{userCmdError}}</b-alert>
			<template #modal-footer="{ ok, cancel }">
				<b-spinner v-show="userCmdLoading>0" small label="Spinning"></b-spinner>
				<b-button size="sm" variant="success" type="submit" form="userCommandEditForm">
					Save
				</b-button>
				<b-button size="sm" @click="cancel()">
					Cancel
				</b-button>
			</template>
		</b-modal>
		<b-modal ref="userCmdDeleteModal" title="Delete command" @ok="deleteUserCmd()">
			<span v-if="userCmdName">Are you sure you want to delete {{userCmdName}}?</span>
			<b-alert variant="danger" :show="!!userCmdError">{{userCmdError}}</b-alert>
			<template #modal-footer="{ok, cancel}">
				<b-spinner v-show="userCmdLoading>0" small label="Spinning"></b-spinner>
				<b-button size="sm" variant="danger" @click="ok()">
					Delete
				</b-button>
				<b-button size="sm" @click="cancel()">
					Cancel
				</b-button>
			</template>
		</b-modal>
	</div>
</template>

<script>
import util from '../util';

const defaultWebhook = () => ({
	enabled: false,
	method: 'POST',
	url: '',
	content_type: 'application/json',
	headers: [],
	body: '',
	secret: '',
});

export default {
	data() { return {
		statuses: [ 'enabled', 'disabled' ],
		httpMethods: [ 'GET', 'POST', 'PUT', 'PATCH', 'DELETE' ],
		contentTypes: [
			{ value: 'application/json', text: 'application/json' },
			{ value: 'application/x-www-form-urlencoded', text: 'application/x-www-form-urlencoded' },
			{ value: 'text/plain', text: 'text/plain' },
		],

		commandError: null,
		commands: null,
		error: null,
		loadingCommand: 0,
		loadingCommands: 0,
		curCmd: null,

		user: {},
		curUserCmd: null,
		userCmdError: null,
		userCmdLoading: 0,
		userCmdName: null,
		userCommandError: null,
		userCommandsLoading: 0,
	}},
	async created() {
		await Promise.all([
			this.loadCommands(),
			this.loadUser(),
		]);
	},
	methods: {

		async loadUser() {
			try {
				this.user = await this.$api.get('userData');
			} catch(e) {
				console.error('Loading user data', e);
			}
		},

		async loadCommands() {
			try {
				this.loadingCommands++;
				this.error = null;
				const [ commands, userCommands ] = await Promise.all([
					this.$api.get('userCommands'),
					this.$api.get('userCustomCommands'),
				]);
				this.commands = [].concat(commands, userCommands.map(c => ({ ...c, isCustom: true }))).map(c => ({ ...c, changingStatus: false }));
			} catch(e) {
				this.error = e;
			} finally {
				this.loadingCommands--;
			}
		},

		async openCommandEditModal(name) {
			try {
				this.commandName = name;
				this.loadingCommand++;
				this.commandError = null;
				this.$refs.commandEditModal.show();
				this.curCmd = await this.$api.get('userCommand', { name });
			} catch(e) {
				console.error('Opening command edit modal', e);
				this.commandError = e;
			} finally {
				this.loadingCommand--;
			}
		},

		async saveCommand() {
			try {
				this.loadingCommand++;
				this.commandError = null;
				await this.$api.post('userCommand', {
					name: this.commandName,
					data: {
						name_override: this.curCmd.name_override,
						help_override: this.curCmd.help_override,
						status: this.curCmd.status,
					},
				});
				this.$refs.commandEditModal.hide();
				this.curCmd = null;
				await this.loadCommands();
			} catch(e) {
				this.commandError = e;
				console.error('Error saving item:', e);
			} finally {
				this.loadingCommand--;
			}
		},

		async setCommandStatus(item) {
			try {
				item.changingStatus = true;
				if(item.isCustom)
					await this.$api.post('userCustomCommandStatus', { name: item.name, status: item.status });
				else
					await this.$api.post('userCommandStatus', { name: item.name, status: item.status });
			} catch(e) {
				console.error('Could not set command status', item.name, item.status, e);
				this.$bvToast.toast('Error while changing command status', {
					title: String(e),
					variant: 'danger',
					solid: true
				});
			} finally {
				item.changingStatus = false;
			}
		},

		async openUserCmdEditModal(name) {
			if(!name && this.user.id && !this.user.is_subscribed) {
				this.$root.$emit('show-upgrade-modal');
				return;
			}
			try {
				this.userCmdError = null;
				this.userCmdLoading++;
				if(name) {
					this.userCmdName = name;
					const cmd = await this.$api.get('userCustomCommand', { name });
					cmd.data.replies = (cmd.data.replies||[]).join('\n');
					if(!cmd.data.webhook) cmd.data.webhook = defaultWebhook();
					this.curUserCmd = cmd;
				} else {
					this.userCmdName = null;
					this.curUserCmd = {
						name: '',
						status: 'enabled',
						data: { replies: '', webhook: defaultWebhook() },
						min_params: -1,
						max_params: -1,
						help: '',
					};
				}
				this.$refs.userCmdEditModal.show();
			} catch(e) {
				this.userCmdError = e.toString();
			} finally {
				this.userCmdLoading--;
			}
		},

		async saveUserCmd() {
			try {
				this.userCmdLoading++;
				this.userCmdError = null;
				await this.$api.post('userCustomCommand', {
					name: this.userCmdName,
					data: {
						name: this.curUserCmd.name,
						status: this.curUserCmd.status,
						data: {
							replies: (this.curUserCmd.data.replies||'').split('\n').filter(a => !!util.trim(a)),
							webhook: this.curUserCmd.data.webhook,
						},
						min_params: this.curUserCmd.min_params,
						max_params: this.curUserCmd.max_params,
						help: this.curUserCmd.help,
					},
				});
				await this.loadCommands();
				this.$refs.userCmdEditModal.hide();
			} catch(e) {
				console.error('Saving user cmd', e);
				this.userCmdError = e;
			} finally {
				this.userCmdLoading--;
			}
		},

		addWebhookHeader() {
			this.curUserCmd.data.webhook.headers.push({ name: '', value: '' });
		},

		removeWebhookHeader(idx) {
			this.curUserCmd.data.webhook.headers.splice(idx, 1);
		},

		openUserCmdDeleteModal(name) {
			this.userCmdName = name;
			this.userCmdError = null;
			this.$refs.userCmdDeleteModal.show();
		},

		async deleteUserCmd() {
			try {
				this.userCmdLoading++;
				this.userCmdError = null;
				await this.$api.delete('userCustomCommand', { name: this.userCmdName })
				this.$refs.userCmdDeleteModal.hide();
				this.loadCommands();
			} catch(e) {
				this.userCmdError = e;
			} finally {
				this.userCmdLoading--;
			}
		},
	},
};
</script>

<style scoped lang="scss">
@import '../plugins/variable.scss';

.command-item {
	background-color: $gray-200;
}
.command-item:nth-child(odd) {
	background-color: $gray-400;
}
.command-item:hover {
	background-color: $gray-600;
}
</style>
